import type { TintPalette } from "./coverTint";

export const GLOW_SPREAD = 31;
export const GLOW_MAX_EXTRA_BLEED = 480;
export const GLOW_MAX_PIXELS = 900_000;
export const GLOW_STATIC_FRAME = 5200;
export const GLOW_OPACITY = 0.78;

export const GLOW_SHADER_PARAMS = {
  colorBack: "#00000000",
  speed: 1,
  thickness: 0.05,
  softness: 0.75,
  intensity: 0.3,
  bloom: 0.5,
  spots: 3,
  spotSize: 0.6,
  pulse: 0,
  smoke: 0.35,
  smokeSize: 0.63,
} as const;

export const FALLBACK_GLOW_COLORS = ["#c9a24e", "#b8744a", "#d9c08a"];

interface Rgb {
  r: number;
  g: number;
  b: number;
}

function clampByte(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

export function parseCssColor(value: string | null | undefined): Rgb | null {
  if (!value) return null;
  const text = value.trim().toLowerCase();
  const hex = text.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (hex) {
    const digits = hex[1].length === 3 ? hex[1].split("").map((char) => char + char).join("") : hex[1];
    return { r: parseInt(digits.slice(0, 2), 16), g: parseInt(digits.slice(2, 4), 16), b: parseInt(digits.slice(4, 6), 16) };
  }
  const rgb = text.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/);
  if (rgb) return { r: clampByte(Number(rgb[1])), g: clampByte(Number(rgb[2])), b: clampByte(Number(rgb[3])) };
  return null;
}

export function toHex({ r, g, b }: Rgb): string {
  return `#${[r, g, b].map((channel) => clampByte(channel).toString(16).padStart(2, "0")).join("")}`;
}

function toHsl({ r, g, b }: Rgb): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === rn) h = (gn - bn) / d + (gn < bn ? 6 : 0);
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return { h: h * 60, s, l };
}

function fromHsl(h: number, s: number, l: number): Rgb {
  const hue = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = l - c / 2;
  const [r1, g1, b1] =
    hue < 60 ? [c, x, 0] : hue < 120 ? [x, c, 0] : hue < 180 ? [0, c, x] : hue < 240 ? [0, x, c] : hue < 300 ? [x, 0, c] : [c, 0, x];
  return { r: (r1 + m) * 255, g: (g1 + m) * 255, b: (b1 + m) * 255 };
}

function tone(color: Rgb, minLightness: number, maxLightness: number, minSaturation: number): Rgb {
  const { h, s, l } = toHsl(color);
  return fromHsl(h, Math.min(0.9, Math.max(s, minSaturation)), Math.min(maxLightness, Math.max(l, minLightness)));
}

export function glowColors(tint: TintPalette | null, accent: string | null | undefined): string[] {
  const brand = parseCssColor(accent);
  const edge = parseCssColor(tint?.edge);
  const glow = parseCssColor(tint?.glow);
  if (!edge || !glow) {
    if (!brand) return [...FALLBACK_GLOW_COLORS];
    const { h, s } = toHsl(brand);
    return [toHex(tone(brand, 0.5, 0.6, 0.45)), toHex(fromHsl(h + 28, Math.max(0.5, s), 0.54)), toHex(fromHsl(h - 24, Math.max(0.45, s), 0.6))];
  }
  const { h, s } = toHsl(glow);
  const companion = fromHsl(h + 38, Math.min(0.85, Math.max(0.55, s)), 0.54);
  const third = brand ? tone(brand, 0.5, 0.6, 0.4) : fromHsl(h - 32, Math.min(0.8, Math.max(0.5, s)), 0.58);
  const colors = [toHex(tone(edge, 0.5, 0.58, 0.5)), toHex(companion), toHex(third)];
  return colors.filter((color, index) => colors.indexOf(color) === index);
}

export interface GlowGeometry {
  bleed: number;
  worldWidth: number;
  worldHeight: number;
  marginX: number;
  marginY: number;
  roundness: number;
  canvasWidth: number;
  canvasHeight: number;
}

export function glowGeometry(width: number, height: number, radius: number, spread = GLOW_SPREAD): GlowGeometry {
  const w = Math.max(1, width);
  const h = Math.max(1, height);
  const worldWidth = w + spread * 2;
  const worldHeight = h + spread * 2;
  const bleed = spread + Math.min(GLOW_MAX_EXTRA_BLEED, Math.ceil(0.4 * Math.min(worldWidth, worldHeight)));
  const half = Math.min(w, h) / 2;
  return {
    bleed,
    worldWidth,
    worldHeight,
    marginX: spread / worldWidth,
    marginY: spread / worldHeight,
    roundness: Math.max(0, Math.min(1, radius / half)),
    canvasWidth: w + bleed * 2,
    canvasHeight: h + bleed * 2,
  };
}

export function glowPixelBudget(viewportWidth: number, lowEnd: boolean): number {
  if (lowEnd || viewportWidth < 768) return Math.round(GLOW_MAX_PIXELS * 0.5);
  return GLOW_MAX_PIXELS;
}

export function cssGlowShadow(colors: string[]): string {
  const [first, second] = colors;
  return `0 0 0 1px color-mix(in srgb, ${first} 55%, transparent), 0 0 22px -2px color-mix(in srgb, ${first} 55%, transparent), 0 0 48px -8px color-mix(in srgb, ${second ?? first} 45%, transparent)`;
}

let webglSupport: boolean | null = null;

export function supportsWebGL2(): boolean {
  if (webglSupport !== null) return webglSupport;
  if (typeof document === "undefined" || typeof window === "undefined" || typeof window.WebGL2RenderingContext === "undefined") {
    webglSupport = false;
    return webglSupport;
  }
  try {
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("webgl2");
    webglSupport = Boolean(context);
    context?.getExtension("WEBGL_lose_context")?.loseContext();
  } catch {
    webglSupport = false;
  }
  return webglSupport;
}

export function resetWebGLSupportCache(): void {
  webglSupport = null;
}
